/**
 * ConfidenceUI.tsx  (config-driven)
 * ─────────────────────────────────────────────────────────────────────────────
 * Surfaces per-value confidence + justification in the review UI. Every
 * attribute, label, weight, threshold, and trace label is derived from the
 * SME-merged dgConfig (confidence_model + enrichment_groups) via
 * resolveConfidenceModel() — nothing here is hardcoded or client-specific.
 *
 * Host passes the SAME dgConfig it already holds. Call resolveConfidenceModel
 * once (e.g. useMemo) and pass the resulting `model` to each component.
 */
import React, { useState } from "react";

const C = {
  ink: "#0f1923", second: "#1e2d3d", muted: "#445566", label: "#2c3e52",
  brand: "#1e3a6e", brandLight: "#dde7f5", brandMid: "#90aed0",
  border: "#c8d4e0", card: "#ffffff", bg: "#eef1f6",
  green: "#166534", greenBg: "#f0fdf4", greenBorder: "#86efac",
  amber: "#9a3412", amberBg: "#fff7ed", amberBorder: "#fdba74",
  red: "#991b1b", redBg: "#fef2f2", redBorder: "#fca5a5",
};

// ── resolved model type ───────────────────────────────────────────────────────
export interface ConfAttr { confKey: string; sourceAttr?: string; label: string; weight: number; }
export interface ConfModel {
  enabled: boolean;
  highMin: number; medMin: number; reviewBelow: number;
  ruleDriven: number; defaultConf: number;
  attributes: ConfAttr[];
  confKeyBySourceAttr: Record<string, string>;
  labels: Record<string, string>;
  traceLabels: Record<string, string>;
}

// Fallback mirrors master defaults; used only when confidence_model is absent.
const FALLBACK_ATTRS: ConfAttr[] = [
  { confKey: "regulatory_frameworks", sourceAttr: "regulatory_framework", label: "Regulatory",     weight: 0.22 },
  { confKey: "sensitivity",           sourceAttr: "sensitivity",          label: "Sensitivity",    weight: 0.20 },
  { confKey: "privacy_category",      sourceAttr: "privacy",              label: "Privacy",        weight: 0.20 },
  { confKey: "ownership",             sourceAttr: "ownership",            label: "Ownership",      weight: 0.12 },
  { confKey: "retention_policy",      sourceAttr: "retention_policy",     label: "Retention",      weight: 0.10 },
  { confKey: "access_control",        sourceAttr: "access_control",       label: "Access Control", weight: 0.08 },
  { confKey: "masking_required",      sourceAttr: "masking_required",     label: "Masking",        weight: 0.04 },
  { confKey: "encryption_required",   sourceAttr: "encryption_required",  label: "Encryption",     weight: 0.04 },
];

export function resolveConfidenceModel(dgConfig: any): ConfModel {
  const cm = dgConfig?.confidence_model ?? {};
  const interp = cm.interpretation ?? {};
  const highMin = Number(interp.high_min ?? 0.85);
  const medMin = Number(interp.medium_min ?? 0.65);
  const reviewBelow = Number(interp.review_below ?? 0.65);

  const catalog: ConfAttr[] = (cm.attributes ?? []).map((a: any) => ({
    confKey: a.conf_key, sourceAttr: a.source_attr, label: a.label ?? a.conf_key,
    weight: Number(a.weight ?? 0),
  }));
  const cat = catalog.length ? catalog : FALLBACK_ATTRS;

  // active = source attr still present in the (already SME-merged) enrichment_groups
  const enabled = new Set<string>();
  const groups = dgConfig?.enrichment_groups;
  let filter = false;
  if (Array.isArray(groups)) {
    filter = true;
    for (const g of groups) for (const a of (g.attributes ?? [])) if (a?.id) enabled.add(a.id);
  }
  let active = cat.filter(a => !filter || !a.sourceAttr || enabled.has(a.sourceAttr));
  if (active.length === 0) active = cat.slice();

  const totalW = active.reduce((s, a) => s + (a.weight || 0), 0) || active.length;
  active = active.map(a => ({ ...a, weight: (a.weight || 1 / active.length) / totalW }));

  const traceLabels: Record<string, string> = {};
  for (const [k, v] of Object.entries(cm.trace_labels ?? {})) if (!k.startsWith("_")) traceLabels[k] = String(v);

  return {
    enabled: cm.enabled !== false,
    highMin, medMin, reviewBelow,
    ruleDriven: Number(cm.rule_driven_confidence ?? 0.95),
    defaultConf: Number(cm.default_confidence ?? 0.6),
    attributes: active,
    confKeyBySourceAttr: Object.fromEntries(active.filter(a => a.sourceAttr).map(a => [a.sourceAttr as string, a.confKey])),
    labels: Object.fromEntries(active.map(a => [a.confKey, a.label])),
    traceLabels,
  };
}

// ── helpers (threshold-aware) ─────────────────────────────────────────────────
export function confLevel(v: number | null | undefined, m: ConfModel) {
  if (v == null || isNaN(v)) return "—";
  return v >= m.highMin ? "High" : v >= m.medMin ? "Medium" : "Low";
}
export function confTone(v: number | null | undefined, m: ConfModel) {
  if (v == null || isNaN(v)) return { color: C.muted, bg: C.bg, border: C.border };
  if (v >= m.highMin) return { color: C.green, bg: C.greenBg, border: C.greenBorder };
  if (v >= m.medMin) return { color: C.amber, bg: C.amberBg, border: C.amberBorder };
  return { color: C.red, bg: C.redBg, border: C.redBorder };
}
export const pct = (v: number | null | undefined) =>
  v == null || isNaN(v) ? "—" : `${Math.round(v * 100)}%`;

function attrConf(field: any, confKey: string) {
  const e = field?.classification_confidence?.[confKey];
  return e && typeof e === "object" ? e : null;
}

// ── 1. inline chip (keyed by enrichment attr.id) ─────────────────────────────
export function AttrConfidenceChip({ field, attrId, model }: { field: any; attrId: string; model: ConfModel; }) {
  const [open, setOpen] = useState(false);
  const confKey = model.confKeyBySourceAttr[attrId];
  const cc = confKey ? attrConf(field, confKey) : null;
  if (!cc || cc.confidence == null) return null;
  const tone = confTone(cc.confidence, model);
  const isRule = cc.source === "rule";
  return (
    <span style={{ position: "relative", display: "inline-flex", alignItems: "center" }}
      onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <span tabIndex={0} onFocus={() => setOpen(true)} onBlur={() => setOpen(false)}
        style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: "help",
          fontSize: "0.62rem", fontWeight: 700, padding: "1px 7px", borderRadius: 20,
          background: tone.bg, color: tone.color, border: `1px solid ${tone.border}`, lineHeight: 1.5, whiteSpace: "nowrap" }}>
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: tone.color }} />
        {pct(cc.confidence)}{isRule && <span title="Set by a governance rule" style={{ fontWeight: 800 }}>·R</span>}
      </span>
      {open && cc.justification && (
        <span role="tooltip" style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 50, width: 260,
          padding: "9px 11px", borderRadius: 8, background: C.ink, color: "#fff", fontSize: "0.72rem",
          lineHeight: 1.5, boxShadow: "0 8px 24px rgba(0,0,0,0.28)", border: `1px solid ${tone.color}` }}>
          <span style={{ display: "block", fontWeight: 700, color: tone.border, marginBottom: 3 }}>
            {confLevel(cc.confidence, model)} · {pct(cc.confidence)}{isRule ? " · rule-driven" : ""}
          </span>
          {cc.justification}
        </span>
      )}
    </span>
  );
}

// ── 2. field roll-up badge + nav dot ──────────────────────────────────────────
export function FieldConfidenceBadge({ field, model, onClick }: { field: any; model: ConfModel; onClick?: () => void; }) {
  const score = field?.field_confidence?.score;
  if (score == null) return null;
  const tone = confTone(score, model);
  const needsReview = score < model.reviewBelow;
  return (
    <button onClick={onClick} title={field?.field_confidence?.summary || "Field classification confidence"}
      style={{ display: "inline-flex", alignItems: "center", gap: 7, cursor: onClick ? "pointer" : "default",
        padding: "4px 11px", borderRadius: 6, fontSize: "0.71rem", fontWeight: 700,
        background: tone.bg, color: tone.color, border: `1px solid ${tone.border}` }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: tone.color }} />
      {pct(score)} confidence<span style={{ opacity: 0.75, fontWeight: 600 }}>· {confLevel(score, model)}</span>
      {needsReview && <span style={{ marginLeft: 2, fontSize: "0.62rem", fontWeight: 800, padding: "0 6px", borderRadius: 20, background: C.red, color: "#fff" }}>REVIEW</span>}
    </button>
  );
}

export function FieldNavConfidenceDot({ field, model }: { field: any; model: ConfModel; }) {
  const score = field?.field_confidence?.score;
  if (score == null || score >= model.reviewBelow) return null;
  return <span title={`Low confidence (${pct(score)}) — needs review`} style={{ fontSize: "0.58rem", fontWeight: 800, color: C.red }}>⚑{pct(score)}</span>;
}

// ── 3. decision-confidence section for the AI Reasoning modal ─────────────────
// Value lookup is a fixed data-contract (agent output shape), not client config;
// host can override via `valueFor` (e.g. pass its getVal) to avoid any local map.
const SCHEMA_PATHS: Record<string, (f: any) => any> = {
  ownership: f => f.classification?.ownership,
  sensitivity: f => f.classification?.sensitivity,
  privacy_category: f => f.classification?.privacy_category,
  regulatory_frameworks: f => f.classification?.regulatory_frameworks,
  retention_policy: f => f.data_protection_controls?.retention_policy,
  masking_required: f => f.data_protection_controls?.masking_required,
  encryption_required: f => f.data_protection_controls?.encryption_required,
  access_control: f => f.data_protection_controls?.access_control,
};
const showVal = (v: any) =>
  v == null || v === "" ? "—" : typeof v === "boolean" ? (v ? "Yes" : "No")
  : Array.isArray(v) ? (v.length ? v.join(", ") : "—") : String(v);

export function ClassificationConfidenceSection({ field, model, valueFor }:
  { field: any; model: ConfModel; valueFor?: (f: any, confKey: string) => any; }) {
  const cc = field?.classification_confidence;
  if (!cc || typeof cc !== "object" || model.attributes.length === 0) return null;

  const rows = model.attributes.map(a => {
    const raw = valueFor ? valueFor(field, a.confKey) : (SCHEMA_PATHS[a.confKey]?.(field));
    const e = cc[a.confKey];
    return { key: a.confKey, label: a.label, value: showVal(raw),
      conf: e?.confidence as number | undefined, why: e?.justification as string | undefined, source: e?.source as string | undefined };
  }).filter(r => r.conf != null || (r.value && r.value !== "—"));
  if (rows.length === 0) return null;

  const overall = field?.field_confidence?.score as number | undefined;
  const oTone = confTone(overall, model);
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <span style={{ width: 22, height: 22, borderRadius: 5, background: C.brandLight, border: `1px solid ${C.brandMid}`,
          display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "0.75rem" }}>🎯</span>
        <span style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: C.brand }}>
          Decision confidence — why each value was chosen</span>
        {overall != null && (
          <span style={{ marginLeft: "auto", fontSize: "0.66rem", fontWeight: 800, padding: "2px 10px", borderRadius: 20,
            background: oTone.bg, color: oTone.color, border: `1px solid ${oTone.border}` }}>
            Overall {pct(overall)} · {confLevel(overall, model)}</span>
        )}
      </div>
      <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden", background: C.card }}>
        {rows.map((r, i) => {
          const tone = confTone(r.conf, model);
          const p = r.conf != null ? Math.round(r.conf * 100) : 0;
          return (
            <div key={r.key} style={{ padding: "11px 14px", borderBottom: i < rows.length - 1 ? `1px solid ${C.border}` : "none", background: i % 2 === 0 ? C.card : "#fafbfd" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: r.why ? 6 : 0 }}>
                <span style={{ fontSize: "0.62rem", fontWeight: 700, color: C.label, textTransform: "uppercase", letterSpacing: "0.06em", minWidth: 96 }}>{r.label}</span>
                <span style={{ fontSize: "0.8rem", fontWeight: 600, color: C.ink, flex: 1, minWidth: 0 }}>{r.value}</span>
                {r.source === "rule" && <span title="Set by a governance rule" style={{ fontSize: "0.58rem", fontWeight: 800, color: C.brand, background: C.brandLight, border: `1px solid ${C.brandMid}`, borderRadius: 4, padding: "0 6px" }}>RULE</span>}
                {r.conf != null && <span style={{ fontSize: "0.72rem", fontWeight: 800, color: tone.color, minWidth: 38, textAlign: "right" }}>{p}%</span>}
              </div>
              {r.conf != null && (
                <div style={{ height: 4, background: C.border, borderRadius: 99, overflow: "hidden", margin: r.why ? "0 0 6px" : 0 }}>
                  <div style={{ width: `${p}%`, height: "100%", background: tone.color, borderRadius: 99 }} />
                </div>
              )}
              {r.why && <div style={{ fontSize: "0.74rem", color: C.second, lineHeight: 1.5, borderLeft: `2px solid ${tone.border}`, paddingLeft: 8 }}>{r.why}</div>}
            </div>
          );
        })}
      </div>
      {field?.field_confidence?.summary && (
        <div style={{ marginTop: 8, fontSize: "0.7rem", color: C.muted, fontStyle: "italic" }}>{field.field_confidence.summary}</div>
      )}
    </div>
  );
}

// ── 4. trace humanizer — config trace_labels first, then generic prettify ─────
function prettify(tok: string, model: ConfModel): string {
  if (model.traceLabels[tok]) return model.traceLabels[tok];
  const eq = tok.match(/^([A-Za-z_][\w.]*)\s*=\s*(.+)$/);
  if (eq) {
    const key = eq[1].replace(/_/g, " ").replace(/\b\w/g, m => m.toUpperCase());
    let val = eq[2];
    if (val.toLowerCase() === "true") val = "Yes"; else if (val.toLowerCase() === "false") val = "No";
    return `${key}: ${val}`;
  }
  const stripped = tok.replace(/^([A-Z]+[0-9]*)_/, "").replace(/_/g, " ");
  return stripped ? stripped.charAt(0).toUpperCase() + stripped.slice(1).toLowerCase() : tok;
}
export function TraceChip({ token, kind, model }: { token: string; kind: "rule" | "signal"; model: ConfModel; }) {
  const label = prettify(token, model);
  const tone = kind === "rule"
    ? { bg: C.brandLight, color: C.brand, border: C.brandMid }
    : /=(true)|High|Restricted/i.test(token) ? { bg: C.redBg, color: C.red, border: C.redBorder }
    : /=(false)|Low|Not Applicable/i.test(token) ? { bg: C.greenBg, color: C.green, border: C.greenBorder }
    : { bg: C.bg, color: C.muted, border: C.border };
  return <span title={token} style={{ fontSize: "0.68rem", fontWeight: 600, padding: "3px 9px", borderRadius: 5, background: tone.bg, color: tone.color, border: `1px solid ${tone.border}`, cursor: "help" }}>{label}</span>;
}